import {
  ApiOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  CodeOutlined,
  DatabaseOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Row, Space, Tag, Typography } from "antd";

import { fetchSystemInfo } from "../api/system";

const { Paragraph, Text, Title } = Typography;

const CHECKPOINTS = [
  "独立用户名与 Argon2 密码散列",
  "服务端会话、CSRF 与登录限流",
  "普通用户、审核员、管理员角色隔离",
  "账号新增、修改、停用与密码重置",
  "登录和账号维护操作完整留痕",
] as const;

export function PlatformStatisticsPage() {
  const systemQuery = useQuery({
    queryKey: ["system-info"],
    queryFn: ({ signal }) => fetchSystemInfo(signal),
  });

  return (
    <main className="status-page">
      <section className="hero-panel">
        <div className="hero-copy">
          <Tag className="hero-kicker" color="blue">
            平台统计 · PLATFORM INSIGHTS
          </Tag>
          <Title level={1}>平台运行状态与安全基线一览。</Title>
          <Paragraph>
            账号登录、服务端安全会话、角色权限与审计事件已经接入。管理员可在用户管理和操作日志中维护账号并核对访问来源。
          </Paragraph>
          <Space wrap>
            <Button type="primary" icon={<ApiOutlined />} href="/docs" target="_blank">
              打开 API 文档
            </Button>
            <Button icon={<CodeOutlined />} href="/healthz/live" target="_blank">
              检查后端存活
            </Button>
          </Space>
        </div>
        <div className="hero-seal" aria-hidden="true">
          <SafetyCertificateOutlined />
          <span>材料留痕</span>
          <small>TRACEABLE BY DESIGN</small>
        </div>
      </section>

      <Row className="status-grid" gutter={[18, 18]}>
        <Col xs={24} md={12} xl={6}>
          <StatusCard
            icon={<ApiOutlined />}
            label="后端 API"
            value={systemQuery.isSuccess ? "在线" : systemQuery.isPending ? "检测中" : "未连接"}
            detail={
              systemQuery.isSuccess
                ? `FastAPI ${systemQuery.data.version}`
                : "启动 PyCharm 后自动重试"
            }
            tone={systemQuery.isSuccess ? "success" : "neutral"}
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <StatusCard
            icon={<CodeOutlined />}
            label="前端运行时"
            value="Vite Dev"
            detail="React 19 · Ant Design 6"
            tone="primary"
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <StatusCard
            icon={<CloudServerOutlined />}
            label="部署编排"
            value="Compose"
            detail="Nginx · API · Worker · Redis"
            tone="warm"
          />
        </Col>
        <Col xs={24} md={12} xl={6}>
          <StatusCard
            icon={<DatabaseOutlined />}
            label="需求基线"
            value={systemQuery.data?.baseline_id ?? "M0-20260819-01"}
            detail="MySQL 迁移与账号模型已就绪"
            tone="primary"
          />
        </Col>
      </Row>

      {systemQuery.isError ? (
        <Alert
          className="connection-alert"
          type="warning"
          showIcon
          title="前端已运行，暂未连接本地 FastAPI"
          description="请在 PyCharm 中运行 backend/app.py；Vite 会把 /api 请求代理到 127.0.0.1:9201。"
          action={
            <Button size="small" onClick={() => void systemQuery.refetch()}>
              重新检测
            </Button>
          }
        />
      ) : null}

      <Row gutter={[18, 18]}>
        <Col xs={24} xl={15}>
          <Card className="runbook-card" variant="borderless">
            <div className="section-heading">
              <div>
                <Text className="section-eyebrow">IDENTITY FOUNDATION</Text>
                <Title level={2}>M2 安全链路</Title>
              </div>
              <Tag variant="filled">数据库支持 RDS MySQL</Tag>
            </div>
            <div className="runbook-list">
              <RunbookStep
                index="01"
                title="账号认证"
                command="Server-side session"
                description="密码仅保存 Argon2 散列，登录 Cookie 为 HttpOnly，写操作校验 CSRF。"
              />
              <RunbookStep
                index="02"
                title="角色授权"
                command="RBAC guard"
                description="后端接口强制鉴权，前端同步隐藏无权模块并阻止越权路由。"
              />
              <RunbookStep
                index="03"
                title="审计留痕"
                command="Append-only events"
                description="登录、退出、改密和用户维护记录操作人、时间、来源 IP、结果与请求 ID。"
              />
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card className="checkpoint-card" variant="borderless">
            <Text className="section-eyebrow">M2 EXIT GATE</Text>
            <Title level={2}>身份安全验收点</Title>
            <div className="checkpoint-list">
              {CHECKPOINTS.map((checkpoint) => (
                <div className="checkpoint-item" key={checkpoint}>
                  <CheckCircleFilled />
                  <span>{checkpoint}</span>
                </div>
              ))}
            </div>
            <div className="protected-resource-note">
              <DatabaseOutlined />
              <div>
                <strong>云资源保护模式</strong>
                <span>RDS 迁移通过独立命令显式执行；OSS 将在附件里程碑接入，当前不写入。</span>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </main>
  );
}

interface StatusCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  detail: string;
  tone: "primary" | "success" | "warm" | "neutral";
}

function StatusCard({ icon, label, value, detail, tone }: StatusCardProps) {
  return (
    <Card className={`status-card status-card--${tone}`} variant="borderless">
      <div className="status-card__top">
        <span className="status-card__icon">{icon}</span>
        <Text>{label}</Text>
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </Card>
  );
}

interface RunbookStepProps {
  index: string;
  title: string;
  command: string;
  description: string;
}

function RunbookStep({ index, title, command, description }: RunbookStepProps) {
  return (
    <div className="runbook-step">
      <span className="runbook-step__index">{index}</span>
      <div>
        <strong>{title}</strong>
        <code>{command}</code>
        <p>{description}</p>
      </div>
    </div>
  );
}
